import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useLanguage } from '../hooks/useLanguage'

interface DateCalendarPickerProps {
  dates: string[]
  selectedDate?: string
  onSelect: (date: string) => void
}

function parseDate(value?: string) {
  const parts = value?.split('.').map(Number)
  if (!parts || parts.length !== 3 || parts.some(Number.isNaN)) return new Date()
  return new Date(parts[0], parts[1] - 1, parts[2])
}

function dateKey(year: number, month: number, day: number) {
  return `${year}.${String(month + 1).padStart(2, '0')}.${String(day).padStart(2, '0')}`
}

export function DateCalendarPicker({ dates, selectedDate, onSelect }: DateCalendarPickerProps) {
  const { lang, t } = useLanguage()
  const selected = parseDate(selectedDate || dates[0])
  const [view, setView] = useState(() => new Date(selected.getFullYear(), selected.getMonth(), 1))
  const available = useMemo(() => new Set(dates), [dates])

  useEffect(() => {
    setView(new Date(selected.getFullYear(), selected.getMonth(), 1))
  }, [selectedDate])

  const year = view.getFullYear()
  const month = view.getMonth()
  const firstWeekday = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells = Array.from({ length: firstWeekday + daysInMonth }, (_, index) => (
    index < firstWeekday ? null : index - firstWeekday + 1
  ))

  return (
    <div className="w-[286px] rounded-xl border border-gray-200 bg-white p-3 shadow-xl dark:border-gray-700 dark:bg-gray-900">
      <div className="mb-3 flex items-center justify-between">
        <button type="button" onClick={() => setView(new Date(year, month - 1, 1))} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold text-gray-900 dark:text-white">
          {lang === 'zh' ? `${year}年${month + 1}月` : view.toLocaleDateString('en', { month: 'long', year: 'numeric' })}
        </span>
        <button type="button" onClick={() => setView(new Date(year, month + 1, 1))} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 text-center text-[11px] text-gray-400">
        {(lang === 'zh' ? ['日', '一', '二', '三', '四', '五', '六'] : ['S', 'M', 'T', 'W', 'T', 'F', 'S']).map((day, index) => (
          <span key={`${day}-${index}`} className="py-1">{day}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-1 text-center">
        {cells.map((day, index) => {
          if (!day) return <span key={`empty-${index}`} />
          const key = dateKey(year, month, day)
          const enabled = available.has(key)
          const active = key === selectedDate
          return (
            <button
              key={key}
              type="button"
              disabled={!enabled}
              onClick={() => onSelect(key)}
              aria-label={enabled ? key : t(`${key} 暂无内容`, `${key} unavailable`)}
              className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium ${
                active
                  ? 'bg-blue-600 text-white'
                  : enabled
                    ? 'text-gray-800 hover:bg-blue-50 hover:text-blue-600 dark:text-gray-100 dark:hover:bg-blue-950'
                    : 'text-gray-300 dark:text-gray-700'
              }`}
            >
              {day}
            </button>
          )
        })}
      </div>
    </div>
  )
}
